import { useState, useEffect } from "react";
import { apiCall } from "../api";
import { Modal } from "./common/Modal";
import { refreshRolesCache } from "../rolesCache";
import { PERMISSION_GROUPS, PERMISSION_LABELS } from "../permissions";

const EMPTY_ROLE = { name: "", displayName: "", level: 30, permissions: [] };

export default function RBACAdmin() {
  const [roles, setRoles] = useState([]);
  const [allPerms, setAllPerms] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [editPerms, setEditPerms] = useState(new Set());
  const [editLevel, setEditLevel] = useState(30);
  const [editDisplayName, setEditDisplayName] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [newRole, setNewRole] = useState(EMPTY_ROLE);

  async function load() {
    try {
      const [rolesData, permsData] = await Promise.all([
        apiCall("GET", "/roles"),
        apiCall("GET", "/roles/permissions-catalog"),
      ]);
      setRoles(rolesData || []);
      setAllPerms(permsData || []);
      if (!selectedId && rolesData && rolesData.length) {
        const sa = rolesData.find(r => r.name === "superadmin") || rolesData[0];
        selectRole(sa);
      }
    } catch (e) { setErr(e?.response?.data?.detail || e?.message || "Load failed"); }
  }

  useEffect(() => { load(); }, []);

  function selectRole(r) {
    setSelectedId(r.id);
    setEditPerms(new Set(r.permissions || []));
    setEditLevel(r.level || 30);
    setEditDisplayName(r.displayName || r.name);
    setDirty(false);
    setErr("");
  }

  function togglePerm(p) {
    if (selected?.name === "superadmin") return; // locked
    setEditPerms(prev => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p); else next.add(p);
      return next;
    });
    setDirty(true);
  }

  const selected = roles.find(r => r.id === selectedId);
  const isSuperAdmin = selected?.name === "superadmin";
  const isSystem = !!selected?.isSystem;

  async function saveCurrent() {
    if (!selected) return;
    setSaving(true); setErr("");
    try {
      const payload = { displayName: editDisplayName, level: editLevel };
      if (!isSuperAdmin) payload.permissions = Array.from(editPerms);
      await apiCall("PUT", `/roles/${selected.id}`, payload);
      await load();
      await refreshRolesCache();
      setDirty(false);
    } catch (e) { setErr(e?.response?.data?.detail || e?.message || "Save failed"); }
    finally { setSaving(false); }
  }

  async function deleteCurrent() {
    if (!selected || isSystem) return;
    if (!window.confirm(`DELETE role "${selected.displayName}"?\n\nThis cannot be undone.`)) return;
    try {
      await apiCall("DELETE", `/roles/${selected.id}`);
      setSelectedId(null);
      await load();
      await refreshRolesCache();
    } catch (e) { alert(e?.response?.data?.detail || e?.message || "Delete failed"); }
  }

  async function createRole() {
    setErr("");
    if (!newRole.name.trim() || !newRole.displayName.trim()) { setErr("Name and Display Name are required"); return; }
    setSaving(true);
    try {
      await apiCall("POST", "/roles", newRole);
      setShowAddModal(false);
      setNewRole(EMPTY_ROLE);
      await load();
      await refreshRolesCache();
    } catch (e) { setErr(e?.response?.data?.detail || e?.message || "Create failed"); }
    finally { setSaving(false); }
  }

  return (
    <div className="space-y-4" data-testid="rbac-admin">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-bold text-gray-800">Roles & Permissions</h3>
          <p className="text-xs text-gray-500">Edit permissions per role. Add custom roles. Super Admin permissions are locked.</p>
        </div>
        <button onClick={() => { setNewRole({ ...EMPTY_ROLE }); setShowAddModal(true); }} className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm font-semibold hover:bg-blue-700" data-testid="add-role-btn">+ Add Custom Role</button>
      </div>

      {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{err}</div>}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="bg-gray-50 px-3 py-2 border-b text-xs font-semibold text-gray-600">Roles</div>
          <div className="divide-y">
            {roles.map(r => (
              <button key={r.id} onClick={() => selectRole(r)} className={`w-full text-left px-3 py-2 hover:bg-blue-50 ${selectedId === r.id ? "bg-blue-50 border-l-4 border-blue-500" : ""}`}>
                <div className="font-semibold text-sm text-gray-800">{r.displayName}</div>
                <div className="text-xs text-gray-500 flex items-center gap-2"><span>Level {r.level}</span>{r.isSystem && <span className="bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded text-[10px]">SYSTEM</span>}<span className="ml-auto">{(r.permissions || []).length} perms</span></div>
              </button>
            ))}
          </div>
        </div>

        <div className="md:col-span-2 bg-white rounded-xl border p-4 space-y-3">
          {!selected ? (
            <div className="text-sm text-gray-400">Select a role to view/edit its permissions.</div>
          ) : (
            <>
              <div className="flex items-center justify-between border-b pb-2">
                <div>
                  <div className="font-bold text-gray-800">{selected.displayName} <span className="text-xs text-gray-400 ml-1">({selected.name})</span></div>
                  {isSuperAdmin && <div className="text-xs text-orange-600 mt-1">{"\u26A0"} Super Admin permissions are locked {"\u2014"} always full access.</div>}
                </div>
                <div className="flex gap-2">
                  {dirty && <button onClick={saveCurrent} disabled={saving} className="bg-green-600 text-white px-3 py-1 rounded text-xs font-semibold hover:bg-green-700 disabled:opacity-50">{saving ? "Saving..." : "Save Changes"}</button>}
                  {!isSystem && <button onClick={deleteCurrent} className="border border-red-500 text-red-600 px-3 py-1 rounded text-xs font-semibold hover:bg-red-50">Delete Role</button>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500">Display Name</label>
                  <input value={editDisplayName} onChange={e => { setEditDisplayName(e.target.value); setDirty(true); }} className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Level (higher = more senior)</label>
                  <input type="number" value={editLevel} onChange={e => { setEditLevel(parseInt(e.target.value) || 0); setDirty(true); }} className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                </div>
              </div>

              <div className="space-y-2">
                {Object.entries(PERMISSION_GROUPS).map(([group, perms]) => (
                  <div key={group} className="border rounded-lg p-2">
                    <div className="text-xs font-semibold text-gray-700 mb-1">{group}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                      {perms.filter(p => allPerms.includes(p)).map(p => {
                        const checked = isSuperAdmin ? true : editPerms.has(p);
                        return (
                          <label key={p} className={`flex items-center gap-2 text-xs px-2 py-1 rounded ${isSuperAdmin ? "bg-gray-50 cursor-not-allowed" : "hover:bg-blue-50 cursor-pointer"}`}>
                            <input type="checkbox" checked={checked} onChange={() => togglePerm(p)} disabled={isSuperAdmin} />
                            <span className="text-gray-800">{PERMISSION_LABELS[p] || p}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {showAddModal && (
        <Modal title="Add Custom Role" onClose={() => setShowAddModal(false)}>
          <div className="space-y-3 p-2">
            {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{err}</div>}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500">Internal Name *</label>
                <input value={newRole.name} onChange={e => setNewRole(r => ({ ...r, name: e.target.value }))} placeholder="e.g. plant_head" className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
                <p className="text-xs text-gray-400 mt-1">Lowercase, no spaces (auto-cleaned on save).</p>
              </div>
              <div>
                <label className="text-xs text-gray-500">Display Name *</label>
                <input value={newRole.displayName} onChange={e => setNewRole(r => ({ ...r, displayName: e.target.value }))} placeholder="e.g. Plant Head" className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Level</label>
                <input type="number" value={newRole.level} onChange={e => setNewRole(r => ({ ...r, level: parseInt(e.target.value) || 0 }))} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
                <p className="text-xs text-gray-400 mt-1">Higher = more senior. Reference: superadmin=100, unit_admin=80, supervisor=60.</p>
              </div>
            </div>
            <p className="text-xs text-gray-500">Permissions can be set after creating the role from the role list.</p>
            <div className="flex gap-2 pt-2">
              <button onClick={createRole} disabled={saving} className="bg-blue-600 text-white px-4 py-2 rounded text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">{saving ? "Creating..." : "Create Role"}</button>
              <button onClick={() => setShowAddModal(false)} className="border border-gray-300 text-gray-600 px-4 py-2 rounded text-sm hover:bg-gray-50">Cancel</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
